<template>
  <component is="div">
    <div class="question__answer-item-wrap">
      <div class="question__answer-item-outer">
        <AnswerTooltip v-if="answer.paused">{{ tooltip }}</AnswerTooltip>

        <!-- Mobile -->
        <div v-if="isMobile" class="question__answer-inner">
          <img
            v-if="answer.thumbnail && !playingVideo"
            class="question__answer-item-image"
            :src="answer.thumbnail"
            alt=""
            @click="playVideo"
          />
          <VideoComponent
            v-if="getVideoFromUrl"
            ref="videoPlayer"
            :style="videoStyle"
            :src="getVideoFromUrl"
            :video-url="getVideoFromUrl"
            :sound="true"
            :autoplay="false"
            :loop="false"
            :alignment="AlignContentType.CENTER"
            :background-color="videoBackgroundColor"
            :width="width"
            :controls="true"
            :opacity="100"
          />
        </div>

        <!-- tablet & desktop -->
        <div v-else class="question__answer-item-inner">
          <img class="question__answer-item-image" :src="answer.thumbnail" alt="" />
          <span v-if="!answer.paused" class="question__answer-item-play" @click="videoModalOpen = true"></span>
        </div>

        <RichTextRenderer
          v-if="answer.text && answer.text !== '<div><br></div>'"
          class="question__answer-item-text"
          :html="answer.text"
          tag="div"
        />

        <div v-if="buttonEnable || isMobile" class="content">
          <div class="content__item content__item--action">
            <UIButton :disabled="answer.paused" class="btn-multiple" @click="onSelectedAnswer(answer)">
              <span v-if="answer.buttonLabel" class="content__item-action-enabled-text">{{
                answer.buttonLabel ?? 'Vote'
              }}</span>
            </UIButton>
          </div>
        </div>
        <slot />
      </div>
    </div>

    <teleport v-if="!isMobile" to="#app">
      <transition name="popover" :appear="true">
        <!-- page id - just a number that's not relating to any real pages-->
        <UIModal v-if="videoModalOpen" :width="width">
          <div class="row section section--category-sections">
            <div
              v-if="videoBackgroundColor"
              class="section__background-color ng-scope"
              :style="`background:${videoBackgroundColor}`"
            ></div>
            <div class="col section__content" style="padding-top: 30px; padding-bottom: 30px">
              <div class="content">
                <div class="content__item content__item--grid grid container">
                  <div class="grid__row row">
                    <div class="grid__col col-md-12">
                      <div class="grid__col-container">
                        <div class="grid__col-addons">
                          <div class="grid__addon grid__addon--lf-icon">
                            <div class="addon lf-icon ng-scope lf-icon--has-action" style="text-align: right">
                              <span
                                class="lf-icon__shape"
                                style="font-size: 40px"
                                :style="{ color: closeColor }"
                                @click="videoModalOpen = false"
                              >
                                <i class="lf-icon__shape-icon fa fa-times"></i>
                              </span>
                            </div>
                          </div>
                          <div
                            v-if="getVideoFromUrl"
                            class="grid__addon grid__addon--video-embed"
                            style="text-align: center"
                          >
                            <VideoComponent
                              :src="getVideoFromUrl"
                              :video-url="getVideoFromUrl"
                              :sound="true"
                              :autoplay="true"
                              :loop="false"
                              :alignment="AlignContentType.CENTER"
                              :background-color="videoBackgroundColor"
                              :width="width"
                              :controls="true"
                              :opacity="100"
                            />
                            <div style="text-align: center">
                              <UIButton v-if="!buttonEnable" :disabled="error" @click="onSelectedAnswer(answer)">
                                {{ buttonLabel }}
                              </UIButton>
                            </div>
                          </div>
                          <div v-else style="text-align: center">Something went wrong trying to load the video.</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </UIModal>
      </transition>
    </teleport>
  </component>
</template>

<script lang="ts">
import type { CSSProperties, PropType } from 'vue';
import { computed, defineComponent, ref } from 'vue';
import AnswerTooltip from './AnswerItemTooltip.vue';
import type { GamePollAnswerState } from '@/src/components/games/poll/Model';
import {
  generateVimeoUrl,
  generateYouTubeUrl,
  getVimeoIdFromUrl,
  getYouTubeVideoIdFromUrl
} from '@/src/utilities/Utilities';
import RichTextRenderer from '@/src/components/RichTextRenderer.vue';
import UIButton from '@/src/components/ui/UIButton.vue';
import UIModal from '@/src/components/ui/UIModal.vue';
import VideoComponent from '@/src/components/components/video/VideoComponent.vue';
import { AlignContentType, VideoFormatType } from '@/src/typings/enums/enums';
import useDevice from '@/src/hooks/useDevice';

export default defineComponent({
  name: 'AnswerVideoItem',
  components: {
    VideoComponent,
    UIModal,
    UIButton,
    RichTextRenderer,
    AnswerTooltip
  },
  props: {
    answer: {
      type: Object as PropType<GamePollAnswerState>,
      required: true
    },
    width: {
      type: String,
      default: '912px'
    },
    error: {
      type: Boolean,
      default: false
    },
    videoBackgroundColor: {
      type: String
    },
    closeColor: {
      type: String
    },
    buttonEnable: {
      type: Boolean,
      default: false
    },
    buttonLabel: {
      type: String
    },
    tooltip: {
      type: String
    }
  },
  emits: ['selectedAnswer'],
  setup(props, context) {
    const videoModalOpen = ref(false);
    const playingVideo = ref(false);
    const videoPlayer = ref<InstanceType<typeof VideoComponent> | null>(null);

    const isMobile = computed(() => {
      return useDevice().isMobile;
    });

    const playVideo = () => {
      if (videoPlayer.value) {
        playingVideo.value = true;
        videoPlayer.value.play();
      }
    };

    const getVideoFromUrl = computed(() => {
      if (props.answer.value.includes(VideoFormatType.YOUTUBE)) {
        const youtubeVideoId = getYouTubeVideoIdFromUrl(props.answer.value);

        if (youtubeVideoId) {
          return generateYouTubeUrl(youtubeVideoId);
        }
      }

      if (props.answer.value?.includes(VideoFormatType.VIMEO)) {
        const vimeoVideoId = getVimeoIdFromUrl(props.answer.value);

        if (vimeoVideoId) {
          return generateVimeoUrl(vimeoVideoId);
        }
      }

      return undefined;
    });

    const videoStyle = computed<CSSProperties>(() => {
      if (playingVideo.value) {
        return {
          opacity: 1,
          pointerEvents: 'auto',
          position: 'relative'
        };
      }

      return {
        opacity: 0,
        pointerEvents: 'none',
        position: 'absolute'
      };
    });

    const onSelectedAnswer = (answer: GamePollAnswerState) => {
      videoModalOpen.value = false;
      context.emit('selectedAnswer', answer);
    };

    return {
      videoStyle,
      playingVideo,
      videoPlayer,
      playVideo,
      isMobile,
      videoModalOpen,
      getVideoFromUrl,
      onSelectedAnswer,
      getYouTubeVideoIdFromUrl,
      generateYouTubeUrl,
      AlignContentType
    };
  }
});
</script>
